import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { HdGatePrimaryAction } from '../hd-bodygraph-chart/hd-bodygraph-chart.component';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '../hd-content-language.service';
import { HdPlanetGridVisualizationMode } from '../hd-bodygraph.models';
import { HD_JUST_NOW_ICONS } from '../../library/hd-chart-cycle-metadata';

export type HdChartViewMode = 'chart' | 'mandala' | 'both';
export type HdChartSideMode = 'design' | 'full' | 'personality';
/** Grootte van de shortcut-bar-knoppen: klein (`sm`) of extra klein (`xs`). */
export type HdChartButtonSize = 'sm' | 'xs';

/**
 * Altijd zichtbare shortcut-bar van de chart-omgeving (spec §8): icon-only
 * acties op de plek van de vroegere controls-bar. Settings zelf leven in het
 * settings-sidepanel; deze balk emit alleen events naar de pagina.
 */
@Component({
  selector: 'hd-chart-shortcut-bar',
  imports: [SharedModule],
  templateUrl: './hd-chart-shortcut-bar.component.html',
  styleUrl: './hd-chart-shortcut-bar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdChartShortcutBarComponent {
  readonly primaryGateAction = input<HdGatePrimaryAction>('configure');
  readonly isFrozen = input(false);
  readonly gameActive = input(false);
  /** Spelmodus aan voor de actieve tab; stuurt de actieve kleurstaat van de game-knop. */
  readonly gameMode = input(false);
  readonly showPlanetGrid = input(false);
  readonly simplePlanetGrid = input(false);
  /** Geavanceerde "Resonance"-variant van de Lunar Planetary Square. */
  readonly advancedLpsGrid = input(false);
  readonly planetGridVisualizationMode = input<HdPlanetGridVisualizationMode>('extended');
  readonly includeNonHdFeatures = input(true);
  readonly showGeneKeys = input(false);
  readonly showHdKeynotes = input(false);
  readonly showCenterCheatSheet = input(false);
  /** Mini-planeetblokjes op de bodygraph (gate-block overlay). */
  readonly showGateBlocks = input(false);
  /** Verplaats-tool voor de sleepbare overlays (planeetblokjes + maan-transit kaarten). */
  readonly moveToolActive = input(false);
  readonly hasPlanetaryActivations = input(false);
  readonly annotationActive = input<boolean>(false);
  readonly viewMode = input<HdChartViewMode>('chart');
  readonly sideMode = input<HdChartSideMode>('full');
  /**
   * Drieluik-weergave (spec 2026-07-31): drie charts naast elkaar. Actief =
   * zijde-, view-mode-, grid-, verplaats- en transit-knoppen disabled (de
   * modus staat vast); de knop zelf is enkel buiten transit-/spelmodus
   * beschikbaar.
   */
  readonly tripleViewActive = input(false);
  /**
   * Maan-transit modus (spec 2026-07-06 §6.1). Actief = view-mode- en
   * simple-grid-knoppen disabled (die staan vast in de modus); available =
   * er is een geladen chart met activaties.
   */
  readonly moonTransitActive = input(false);
  readonly moonTransitAvailable = input(false);
  readonly justNowTransitActive = input(false);
  readonly justNowTransitAvailable = input(false);
  readonly justNowTransitLoading = input(false);
  readonly justNowTransitStatusTooltip = input<string | null>(null);
  /**
   * Locks & Keys-modus (spec 2026-08-27): gele lock-vensters + periode-keys op
   * de mandala met het gedockte infopaneel. Available = geen cycle-/composite-
   * tab; de neutrale (algemene) weergave werkt ook zonder geladen chart.
   */
  readonly locksKeysActive = input(false);
  readonly locksKeysAvailable = input(false);
  /** Actieve tab toont een cycle chart (zie {@link cycleOrCompositeActive}). */
  readonly cycleChartActive = input(false);
  /** Actieve tab toont een composite-/relatiechart (zie {@link cycleOrCompositeActive}). */
  readonly compositeChartActive = input(false);
  /** Er zijn zwevende info-panelen die gedockt kunnen worden (groep 4). */
  readonly dockAllAvailable = input(false);
  /** Er zijn gedockte info-panelen die losgemaakt kunnen worden (groep 4). */
  readonly undockAllAvailable = input(false);
  /** Er zijn open info-panelen (zwevend óf gedockt) die gesloten kunnen worden (groep 4). */
  readonly closeAllAvailable = input(false);
  /** Transits-paneel (Nu & Straks + ephemeris-lanes) open in de chart-omgeving (besluit 2). */
  readonly transitsActive = input(false);
  readonly buttonSize = input<HdChartButtonSize>('xs');
  readonly showZodiacRing = input(false);
  readonly showQuarterRing = input(false);
  readonly showIchingRing = input(false);
  readonly showGodheadRing = input(false);
  readonly showSideStats = input(true);
  /**
   * Door de pagina gemeten: de zij-statistieken passen onder de geavanceerde
   * Resonance-LPS zonder dat de chart-kolom gaat scrollen (QHD en hoger).
   */
  readonly sideStatsFitAdvancedLps = input(false);
  readonly canSaveChart = input(false);
  /** Chart delen via link (spec 2026-10-20): enkel op een opgeslagen eigen chart. */
  readonly canShareChart = input(false);
  /**
   * Mobiele toolbar (docs/MOBIEL.md §9 besluit 2): op phone-breedte een
   * compacte balk met de essentiële acties — zijde-cyclus, mandala-toggle,
   * ring-cyclus, transit-modi, Locks & Keys, chart manager en een ⋮-menu voor
   * de rest. De pagina zet deze input op basis van de ViewportService.
   */
  readonly mobile = input(false);

  readonly newChartRequested = output<void>();
  readonly saveChartRequested = output<void>();
  /** Deelknop: de pagina opent de deeldialoog voor de actieve (opgeslagen) chart. */
  readonly shareChartRequested = output<void>();
  readonly chartManagerRequested = output<void>();
  /** Game-knop: de pagina opent de spelkeuze-dialog. */
  readonly gameRequested = output<void>();
  /** Just Now Transit: het huidige moment (enkel personality) als eigen tab. */
  readonly justNowRequested = output<void>();
  /** Zelfde icoon als "Just Now Transit" in de chart manager en de Cycles-sectie. */
  protected readonly justNowTransitIcon = HD_JUST_NOW_ICONS.transit;
  readonly annotateToggled = output<void>();
  readonly clearAnnotationsRequested = output<void>();
  readonly resetRequested = output<void>();
  readonly primaryGateActionChange = output<HdGatePrimaryAction>();
  readonly dockAllPanelsRequested = output<void>();
  readonly undockAllPanelsRequested = output<void>();
  readonly closeAllPanelsRequested = output<void>();
  readonly planetGridToggled = output<void>();
  readonly simplePlanetGridToggled = output<void>();
  readonly advancedLpsGridToggled = output<void>();
  readonly visualizationModeToggled = output<void>();
  readonly geneKeysToggled = output<void>();
  readonly hdKeynotesToggled = output<void>();
  readonly centerCheatSheetToggled = output<void>();
  readonly gateBlocksToggled = output<void>();
  readonly moveToolToggled = output<void>();
  /** Rechterklik op de verplaats-knop → contextmenu (reset per zichtbare laag). */
  readonly moveToolContextMenuRequested = output<{ x: number; y: number }>();
  readonly unfreezeRequested = output<void>();
  readonly stopRequested = output<void>();
  readonly viewModeChange = output<HdChartViewMode>();
  readonly sideModeChange = output<HdChartSideMode>();
  readonly tripleViewToggled = output<void>();
  readonly moonTransitToggled = output<void>();
  readonly justNowTransitToggled = output<void>();
  readonly locksKeysToggled = output<void>();
  readonly transitsToggled = output<void>();
  readonly zodiacRingToggled = output<void>();
  readonly quarterRingToggled = output<void>();
  readonly ichingRingToggled = output<void>();
  readonly godheadRingToggled = output<void>();
  readonly sideStatsToggled = output<void>();
  /** Mobiel: één stap verder in de ring-cyclus (geen → kwartaal → I-Tjing → godhead → dierenriem). */
  readonly ringCycleRequested = output<void>();
  /** Mobiel: ⋮-menu gevraagd — de pagina opent het contextmenu op deze positie. */
  readonly moreRequested = output<{ x: number; y: number }>();

  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  /**
   * Cycle- óf composite-tab (feedback 2026-08-23): die tabs tonen vaste
   * "D · P"-grids voor beide kanten en een vaste twee-kleurenoverlay op de
   * chart. Grid-varianten (Resonance-LPS, PHS-weergave) en de blokjes-laag
   * hebben daar geen effect en staan dus disabled; het drieluik is er wél
   * beschikbaar (bron 1 | combinatie | bron 2).
   */
  protected readonly cycleOrCompositeActive = computed(() =>
    this.cycleChartActive() || this.compositeChartActive(),
  );
  protected readonly tripleViewAvailable = computed(() =>
    !this.moonTransitActive() && !this.justNowTransitActive() && !this.locksKeysActive()
    && !this.gameActive(),
  );
  /**
   * Zijde-knop-tooltips: op gecombineerde (cycle-/composite-)tabs kiezen de
   * knoppen een kant (links = chart 1/natal, rechts = chart 2/cyclusmoment,
   * bijstuurronde 7) i.p.v. een design/personality-zijde.
   */
  protected sideTooltipKey(side: HdChartSideMode): string {
    const combined = this.cycleOrCompositeActive();
    switch (side) {
      case 'design':
        return combined
          ? 'humanDesign.chart.shortcutBar.sideLeftCombinedTooltip'
          : 'humanDesign.chart.shortcutBar.sideDesignTooltip';
      case 'personality':
        return combined
          ? 'humanDesign.chart.shortcutBar.sideRightCombinedTooltip'
          : 'humanDesign.chart.shortcutBar.sidePersonalityTooltip';
      default:
        return combined
          ? 'humanDesign.chart.shortcutBar.sideFullCombinedTooltip'
          : 'humanDesign.chart.shortcutBar.sideFullTooltip';
    }
  }
  protected readonly mandalaOnlyAvailable = computed(() =>
    (!this.justNowTransitActive() || this.locksKeysActive()) && !this.tripleViewActive(),
  );
  protected readonly mandalaRingsAvailable = computed(() =>
    this.viewMode() !== 'chart' && !this.tripleViewActive(),
  );
  protected readonly centerCheatSheetAvailable = computed(() =>
    this.hasPlanetaryActivations() && this.viewMode() === 'chart',
  );
  /**
   * Blokjes-laag: niet in het drieluik (v1, spec §3.2) en niet op cycle-/
   * composite-tabs — de pagina schakelt een nog actieve laag daar zelf uit.
   */
  protected readonly gateBlocksAvailable = computed(() =>
    this.hasPlanetaryActivations() && !this.tripleViewActive() && !this.cycleOrCompositeActive(),
  );
  protected readonly moveToolAvailable = computed(() =>
    this.hasPlanetaryActivations()
    // Just-now overlay meegerekend (spec notable moments §4.1): de kaartlaag
    // is sinds die ronde net zo sleepbaar als de maan-transit-kaarten.
    && (this.showGateBlocks() || this.moonTransitActive() || this.justNowTransitActive())
    && !this.tripleViewActive(),
  );
  protected readonly planetGridControlsAvailable = computed(() =>
    this.hasPlanetaryActivations() && this.showPlanetGrid(),
  );
  protected readonly simplePlanetGridAvailable = computed(() =>
    this.planetGridControlsAvailable() && !this.moonTransitActive() && !this.tripleViewActive()
    && !this.cycleOrCompositeActive(),
  );
  /**
   * Knoppen van de uitgebreide grid (PHS-weergave, Resonance-LPS, GK,
   * zij-statistieken): niet in de eenvoudige grid en niet op cycle-/composite-
   * tabs — daar staan vaste D·P-grids die geen van deze opties kennen.
   */
  protected readonly extendedPlanetGridControlsAvailable = computed(() =>
    this.planetGridControlsAvailable() && !this.simplePlanetGrid() && !this.cycleOrCompositeActive(),
  );
  protected readonly advancedLpsGridAvailable = computed(() =>
    this.extendedPlanetGridControlsAvailable() && !this.moonTransitActive() && !this.tripleViewActive(),
  );
  /**
   * De zij-statistieken stromen onder het design-grid. Naast de geavanceerde
   * Resonance-LPS passen ze enkel wanneer de pagina gemeten heeft dat er
   * verticaal ruimte is (`sideStatsFitAdvancedLps`, bv. QHD); anders blijft de
   * knop daar disabled (de statistieken komen automatisch terug bij het
   * terugschakelen naar de gewone LPS-view).
   */
  protected readonly sideStatsAvailable = computed(() =>
    this.extendedPlanetGridControlsAvailable() && (!this.advancedLpsGrid() || this.sideStatsFitAdvancedLps()),
  );
  /**
   * In mandala-only tonen enkel de planeetgrids keynotes: de uitgebreide én de
   * eenvoudige lijst. De Just Now-grids en de vaste D·P-grids van cycle-/
   * composite-tabs kennen ze niet.
   */
  protected readonly hdKeynotesAvailable = computed(() =>
    this.viewMode() !== 'mandala'
    || (this.planetGridControlsAvailable() && !this.cycleOrCompositeActive() && !this.justNowTransitActive()),
  );

  protected t(key: string, params?: Record<string, unknown>): string {
    return this.transloco.translate(key, params, this.lang());
  }

  /** Tooltip-suffix voor knoppen die op cycle-/composite-tabs geen effect hebben. */
  protected cycleCompositeLockedSuffix(): string {
    return this.cycleOrCompositeActive()
      ? this.t('humanDesign.chart.shortcutBar.cycleCompositeLockedSuffix')
      : '';
  }

  protected onPrimaryGateActionClick(action: HdGatePrimaryAction): void {
    if (this.primaryGateAction() !== action) {
      this.primaryGateActionChange.emit(action);
    }
  }

  protected onViewModeClick(mode: HdChartViewMode): void {
    if (this.viewMode() !== mode) {
      this.viewModeChange.emit(mode);
    }
  }

  /**
   * Chart-knop: de gewone "enkel chart"-keuze; ín de maan-transit een
   * aan/uit-toggle voor de chart binnen het wiel (finetune-ronde 5) — hij
   * wisselt tussen 'both' en 'mandala'. Locks & Keys deed dat ook
   * (bijstuurronde 3), maar sinds de L&K-markeringen op de chart zelf
   * (2026-10-10) betekent de knop daar weer "mandala uit": de chart met
   * ringen en badges volstaat dan.
   */
  protected onChartViewButtonClick(): void {
    if (this.moonTransitActive()) {
      this.viewModeChange.emit(this.viewMode() === 'both' ? 'mandala' : 'both');
      return;
    }
    this.onViewModeClick('chart');
  }

  /** Actieve kleurstaat van de chart-knop: in de maan-transit = "chart zichtbaar in het wiel". */
  protected chartViewButtonActive(): boolean {
    return this.moonTransitActive()
      ? this.viewMode() === 'both'
      : this.viewMode() === 'chart';
  }

  protected chartViewTooltipKey(): string {
    if (this.moonTransitActive()) {
      return 'humanDesign.chart.shortcutBar.viewChartMoonTooltip';
    }
    // Locks & Keys is leidend bij de combinatie met Just Now: daar is het
    // gewoon "enkel chart" (mandala uit).
    if (this.justNowTransitActive() && !this.locksKeysActive()) {
      return 'humanDesign.chart.shortcutBar.viewChartJustNowTooltip';
    }
    return 'humanDesign.chart.shortcutBar.viewChartTooltip';
  }

  protected bothViewTooltipKey(): string {
    return this.justNowTransitActive()
      ? 'humanDesign.chart.shortcutBar.viewBothJustNowTooltip'
      : 'humanDesign.chart.shortcutBar.viewBothTooltip';
  }

  protected justNowTransitTooltip(): string {
    const activeStatus = this.justNowTransitStatusTooltip();
    if (this.justNowTransitActive() && activeStatus != null) {
      return activeStatus;
    }
    return this.t(this.justNowTransitAvailable()
      ? 'humanDesign.chart.shortcutBar.justNowTransitTooltip'
      : 'humanDesign.chart.shortcutBar.justNowTransitUnavailableTooltip');
  }

  protected onSideModeClick(mode: HdChartSideMode): void {
    if (this.sideMode() !== mode) {
      this.sideModeChange.emit(mode);
    }
  }

  /** Rechterklik op de verplaats-knop: native menu onderdrukken, positie doorgeven. */
  protected onMoveToolContextMenu(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.moveToolContextMenuRequested.emit({ x: event.clientX, y: event.clientY });
  }

  // ── Mobiele toolbar ──────────────────────────────────────────────────────

  /** Mobiel: mandala-knop actief zodra het wiel zichtbaar is (mandala of both). */
  protected readonly mobileMandalaActive = computed(() => this.viewMode() !== 'chart');

  /** Mobiel: minstens één mandala-ring zichtbaar (kleurstaat van de ring-cyclus-knop). */
  protected readonly anyRingVisible = computed(() =>
    this.showQuarterRing() || this.showIchingRing() || this.showGodheadRing() || this.showZodiacRing(),
  );

  /** Korte label van de huidige zijde-modus op de mobiele cyclus-knop. */
  protected mobileSideLabel(): string {
    switch (this.sideMode()) {
      case 'design':
        return 'D';
      case 'personality':
        return 'P';
      default:
        return 'D+P';
    }
  }

  /** Mobiel: elke tap stapt door de zijden — beide → design → personality → beide. */
  protected onMobileSideCycle(): void {
    const next: Record<HdChartSideMode, HdChartSideMode> = {
      full: 'design',
      design: 'personality',
      personality: 'full',
    };
    this.sideModeChange.emit(next[this.sideMode()]);
  }

  /**
   * Mobiel: mandala toevoegen/verwijderen. Buiten de wiel-modi wisselt de knop
   * chart ↔ mandala (op phone geen 'both': de ingebedde bodygraph wordt daar
   * onleesbaar klein); binnen de maan-transit, die het wiel nodig heeft,
   * toggelt hij de chart ín het wiel, net als de desktop-chartknop. In Locks &
   * Keys stapt hij door de drie weergaven (wiel met chart → enkel wiel →
   * enkel chart), want daar mag de mandala ook uit (2026-10-10).
   */
  protected onMobileMandalaToggle(): void {
    if (this.moonTransitActive()) {
      this.viewModeChange.emit(this.viewMode() === 'both' ? 'mandala' : 'both');
      return;
    }
    if (this.locksKeysActive()) {
      const next: Record<HdChartViewMode, HdChartViewMode> = { both: 'mandala', mandala: 'chart', chart: 'both' };
      this.viewModeChange.emit(next[this.viewMode()]);
      return;
    }
    this.viewModeChange.emit(this.viewMode() === 'chart' ? 'mandala' : 'chart');
  }

  protected onMobileMore(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    // Menupositie: linksonder de knop, zodat het menu binnen beeld opent.
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    this.moreRequested.emit({ x: Math.max(8, rect.right - 200), y: rect.bottom + 4 });
  }
}
